import { widgetLayoutSchema } from '@/config/schema';
import { useWidgetLayout } from '@/hooks/useWidgetLayout';
import type { WidgetProps } from '@/widgets/registry';

/**
 * Stand-in for a widget type the migration knows about but has not ported yet
 * (`pendingWidgetTypes` in the registry).
 *
 * This is what makes SYM-491 reviewable on its own: without it, a correct renderer and a
 * completely broken one both produce an empty page. With it, `/szeged_2` shows six boxes in
 * config order, at the spacing the real widgets will occupy, each labelled with the type it
 * is standing in for and the ticket that will replace it.
 *
 * It deliberately runs `useWidgetLayout(options.layout)` like a real widget, so the box
 * geometry — padding, background, corner radius, the `component-column` gutter — is the
 * geometry the finished widget will have. A placeholder that ignored `options.layout` would
 * make the layout comparison against the legacy app (SYM-506) meaningless.
 *
 * Rendered directly by `WidgetSlot` rather than through the registry, because it needs the
 * config `type` and the owning ticket, which `WidgetProps` intentionally does not carry.
 */
export type PlaceholderWidgetProps = WidgetProps & {
  /** The config `type` this is standing in for. */
  type: string;
  /** Linear ticket that implements it, e.g. `SYM-495`. */
  ticket: string;
};

export function PlaceholderWidget({ componentId, options, type, ticket }: PlaceholderWidgetProps) {
  // The same parse a real widget does for its own layout, and the same degradation: a
  // malformed `options.layout` costs this widget its styling, not its existence.
  const parsedLayout = widgetLayoutSchema.safeParse(options.layout ?? {});
  const { className, style } = useWidgetLayout(
    parsedLayout.success ? parsedLayout.data : undefined,
  );

  const topic = typeof options.topic === 'string' ? options.topic : undefined;

  return (
    <div
      className={className}
      style={style}
      data-placeholder-for={type}
      data-component-id={componentId}
    >
      {/*
       * `border-current` rather than a palette colour: the body font colour comes from
       * `layout.fontColor` and differs per client, so a fixed border colour would be
       * invisible on at least one of the seven configs.
       *
       * No `font-mono` on the id/topic lines — `global.css` sets the brand font with a
       * universal selector, which beats every font utility (MIGRATION decision #13).
       */}
      <div className="w-full rounded-xl border-2 border-dashed border-current p-4 text-center">
        <div className="text-xl">{type}</div>
        <div className="mt-1 text-sm opacity-70">not yet implemented — {ticket}</div>
        {topic !== undefined && <div className="mt-1 text-xs opacity-50">topic: {topic}</div>}
        <div className="mt-1 text-xs opacity-40">{componentId}</div>
      </div>
    </div>
  );
}
